import React, {useContext, useEffect, useState} from "react";
import dynamic from 'next/dynamic';
import {Box, Text} from "grommet";
import {handleErrors} from "../../utils/errors";
import useSWR from "swr";
import {NotificationsContext} from "../NotificationsProvider";
import PaymentConfirmation from "./PaymentConfirmation";
import PaymentFinalizing from "./PaymentFinalizing";
/*
    Static on purpose, unlike the payment forms below it. This is the checkout's
    first screen, so a dynamic import guaranteed a gap: the server-rendered
    skeleton was torn down on hydration and nothing stood in its place until the
    chunk arrived, collapsing the panel by 75px and then pushing it back. Its
    dependencies are already in the page's graph, so this costs nothing.
*/
import PaymentMethodsMenu from "./PaymentMethodsMenu";
import CheckoutCard from "./CheckoutCard";
import {forgetCapturedPayment, readCapturedPayment, rememberCapturedPayment} from "../../utils/checkoutPayment";
import { formatCurrency } from "../../utils/numbers";
import {
    faCreditCard as IconCreditCard,
    faBuildingColumns as IconBank,
    faBoltLightning as IconWire,
    faMoneyCheckDollarPen as IconCheque,
} from '@fortawesome/pro-light-svg-icons';
import {
    QUICK_PAY_STEP,
    AMOUNT_SELECTION_STEP,
    PAYNENT_METHOD_SELECTION_STEP,
    CHECKOUT_TYPES,
    PAYMENT_METHOD_STEP,
    PAYMENT_CONFIRMATION_STEP,
    CHECKOUT_AMOUNT
} from "../../constants/Checkout";
import { getInvoiceAmount } from "../../utils/invoice";

const StripeForm = dynamic(() => import("./StripeForm"));
const CheckForm = dynamic(() => import("./CheckForm"));
const InternationalWireForm = dynamic(() => import("./InternationalWireform"));
const BankTransferForm = dynamic(() => import("./BankTransferForm"));
const AmountSelectionMenu = dynamic(() => import("./AmountSelectionMenu"));
const QuickPayMenu = dynamic(() => import("./QuickPayMenu"));

const paymentMethodsSettings = [
    {
        paymentMethod: 'bank_transfer',
        serviceName: 'Bank transfer',
        component: BankTransferForm,
        icon: IconBank,
        showOption: (methods) => {
            // Show bank transfer if it's enabled AND either:
            // 1. It's ACH type, OR
            // 2. Bank debit is enabled (since BankTransferForm wraps both ACH and bank debit)
            const isBankTransferACH = methods?.payment_methods?.bank_transfer?.enabled;
            const isBankDebitEnabled = methods?.payment_methods?.bank_debit?.enabled;
            return methods && (isBankTransferACH || isBankDebitEnabled)
        }
    },
    {
        paymentMethod: 'international_wire',
        serviceName: 'Wire transfer',
        component: InternationalWireForm,
        icon: IconWire,
        showOption: (methods) => {
            return methods && methods?.payment_methods?.bank_transfer?.enabled && methods?.payment_methods?.bank_transfer?.details?.type === 'international_wire'
        }
    },
    {
        paymentMethod: 'lockbox',
        serviceName: 'Paper check',
        component: CheckForm,
        icon: IconCheque
    },
    {
        paymentMethod: "card_processing",
        serviceName: 'Card',
        processor: 'stripe',
        component: StripeForm,
        skipFetchingOptions: true,
        icon: IconCreditCard
    },
];

const getCheckoutType = ({isBulkPay, isMilestoneInvoice}) => {
    if (isBulkPay) return CHECKOUT_TYPES.BULK_PAY;
    if (isMilestoneInvoice) return CHECKOUT_TYPES.MILESTONE;
    return CHECKOUT_TYPES.SINGLE;
}

// figuring out the steps that checkout should show
const getSteps = ({type, isSinglePaymentMethod, hasQuickPayEnabled, invoiceData}) => {
    let steps = [];

    const {
        total_overdue_milestone_amount: totalOverdue,
        total_due_milestone_amount: totalDue,
        amount: totalOpenBalance
    } = invoiceData.balance_net_pending_payment || {};

    const {
        reached_max_attempts: reachedMaximumQPAttempts
    } = invoiceData.quick_pay || {};

    if (hasQuickPayEnabled && type !== CHECKOUT_TYPES.BULK_PAY && !reachedMaximumQPAttempts) {
        // check that there's an amount to charge
        const hasOverdueBalance = !(type === CHECKOUT_TYPES.MILESTONE) || (totalOverdue > 0 || totalDue > 0);
        hasOverdueBalance && steps.push(QUICK_PAY_STEP);
    }

    switch (type) {
    case CHECKOUT_TYPES.SINGLE:
    case CHECKOUT_TYPES.BULK_PAY:
        break;
    case CHECKOUT_TYPES.MILESTONE:
        // Filter out all the 0 and count how many unique values there are amount overdue, due, and total open balance
        if ([...new Set([totalOverdue, totalDue, totalOpenBalance].filter(amount => amount > 0))].length === 1) {
            break;
        }
        // if overdue == due == total open balance, skip amount selection
        if (totalOverdue > 0 && totalDue > 0  && totalOpenBalance > 0 && (totalOverdue === totalDue) && (totalDue === totalOpenBalance)) {
            break;
        }
        steps.push(AMOUNT_SELECTION_STEP); // if a milestone checkout only has one payment method, it's treated as single checkout
        break;

    }
    if (!isSinglePaymentMethod) steps.push(PAYNENT_METHOD_SELECTION_STEP);
    return steps.concat([PAYMENT_METHOD_STEP, PAYMENT_CONFIRMATION_STEP]); // universal across all modes
}

const getPrevStep = (steps, currentStep) => {
    const ind = steps.indexOf(currentStep);
    if (ind <= 0) return null;
    return steps[ind-1];
}

const getNextStep = (steps, currentStep) => {
    const ind = steps.indexOf(currentStep);
    if (ind === -1 || ind >= (steps.length - 1)) return null;
    return steps[ind+1];
}

const getDefaultAmountSelectionOption = (balanceNetPendingPayment) => {
    const {
        total_overdue_milestone_amount: totalOverdue,
        total_due_milestone_amount: totalDue,
    } = balanceNetPendingPayment;
    if (totalOverdue > 0) return CHECKOUT_AMOUNT.TOTAL_PAST_DUE;
    if (totalDue > 0) return CHECKOUT_AMOUNT.TOTAL_DUE;
    return CHECKOUT_AMOUNT.TOTAL_OPEN_BALANCE;
}

export const getPaymentMethodsToDisplay = ({paymentMethods}) => {
    const isInternationalWire = paymentMethods?.payment_methods?.bank_transfer?.details?.type === 'international_wire';

    return (
        paymentMethodsSettings.filter(
            method => {
                // if payment method not enabled, do not display
                if (!paymentMethods ||
                        (method?.showOption ?
                            !method.showOption(paymentMethods) :
                            (!paymentMethods?.payment_methods?.[method.paymentMethod] || !paymentMethods?.payment_methods?.[method.paymentMethod]?.enabled)))
                    return false;

                // if payment method is not card processing, no further checks. Enable it in checkout menu
                if (method.paymentMethod !== "card_processing")
                    return true;
                // if payment method is card processing and processor name matches, display
                return paymentMethods?.payment_methods?.[method.paymentMethod]?.processor === method?.processor;
            }
        )
    );
}

const Checkout = ({
    invoiceToken,
    invoiceData,
    paymentMethods,
    invoiceTokens,
    uuid,
    recipientEmailAddress,
    onBackFromCheckout, // callback for when user intends to go back to the screen before checkout
    onReturnToMainMenu,
    balance,
    printOptions,
    onPaymentSuccess,
    requireQuickPayOnCheckout,
    optionalQuickPayOnCheckout,
    // The checkout special cases
    isBulkPay = false,
    isMilestoneInvoice=false
}) => {
    let amount;
    let currency = 'USD';
    if (isBulkPay) {
        amount = balance?.amount;
        currency = balance?.currency; // single invoice case
    } else {
        let {
            balance: {
                currency: invoiceCurrency
            },
            balance_net_pending_payment: {
                amount: invoiceAmount
            },
        } = invoiceData || {};
        currency = invoiceCurrency;
        amount = invoiceAmount;
    }
    const [checkoutData, setCheckoutData] = useState(undefined);
    const methods = paymentMethods ? getPaymentMethodsToDisplay({paymentMethods}) : [];
    const isSinglePaymentMethod = methods && methods.length === 1;
    const hasQuickPayFeatureEnabled = !!invoiceData?.quick_pay?.feature_enabled;
    const hasQuickPayEnabled = hasQuickPayFeatureEnabled && !!invoiceData?.quick_pay?.enabled;
    const notificationCtx = useContext(NotificationsContext);
    const [selectedMethod, setSelectedMethod] = useState(isSinglePaymentMethod ? methods[0] : undefined); // which payment method is selected
    const [paymentConfirmationData, setPaymentConfirmationData] = useState(undefined); // data we get when payment is successful
    const [descriptionTextUnderTotal, setDescriptionTextUnderSubtext] = useState(); // in the case of card processing, the total changes
    const [amountToPay, setAmountToPay] = useState(amount); // the total that the customer is going to pay, also the amount we display on the top of the checkout widget
    const checkoutType = getCheckoutType({
        isBulkPay,
        isMilestoneInvoice
    });
    const steps = getSteps({type: checkoutType, isSinglePaymentMethod, hasQuickPayEnabled, invoiceData});

    const [currentStep, setCurrentStep] = useState(steps[0]); // keeping track of which screen we're showing
    const checkoutUuid = uuid || invoiceData?.recipient_uuid;

    /*
        Set the moment Stripe confirms a charge, and only cleared once our backend has
        recorded it. While it is set the payment form is not rendered at all, which is
        what makes a second submission (and therefore a second PaymentIntent, and
        therefore a duplicate charge) structurally impossible rather than merely
        disabled — a disabled button doesn't survive the form remounting.
    */
    const [capturedPayment, setCapturedPayment] = useState(undefined);

    // Resume after a reload or a reopened tab: a captured-but-unrecorded payment must
    // put the customer back on the finalizing screen, never on a fresh payment form.
    useEffect(() => {
        const pending = readCapturedPayment(checkoutUuid);
        if (pending) setCapturedPayment(pending);
    }, [checkoutUuid]);

    const handlePaymentCaptured = (payload) => {
        rememberCapturedPayment(checkoutUuid, payload);
        setCapturedPayment(payload);
    };

    const handlePaymentRecorded = (data) => {
        forgetCapturedPayment(checkoutUuid);
        const confirmationData = {
            ...(capturedPayment?.confirmation || {}),
            ...(data?.fee !== undefined ? {fee: data.fee} : {}),
            ...(data?.amount !== undefined ? {amount: data.amount} : {})
        };
        setPaymentConfirmationData(confirmationData);
        onPaymentSuccess && onPaymentSuccess(confirmationData);
        setCapturedPayment(undefined);
        setCurrentStep(PAYMENT_CONFIRMATION_STEP);
    };

    const [balanceType, setBalanceType] = useState(
        checkoutType === CHECKOUT_TYPES?.MILESTONE ?
            getDefaultAmountSelectionOption(invoiceData?.balance_net_pending_payment) :
            CHECKOUT_AMOUNT.TOTAL_OPEN_BALANCE
    ); // indicate whether or not customer is paying minimum due or the full balance. Minimum due is for milestone case only

    const fetcher = (url) => fetch(url)
        .then(handleErrors)
        .catch(() => notificationCtx.showNotification({message: "We weren't able to fetch payment options. Please refresh the page and try again.", type: 'error'}));

    useSWR(
        selectedMethod && !selectedMethod.skipFetchingOptions?
            `/api/checkout/${uuid || invoiceData?.recipient_uuid}/${selectedMethod.paymentMethod}` :
            null,
        fetcher,
        {
            revalidateOnFocus: false,
            onSuccess: data => setCheckoutData(data)
        },
    );
    const renderCheckout = ({
        methods,
        selectedMethod,
        checkoutData,
        isBulkPay,
        isMilestoneInvoice,
        onAmountToPayChange,
        paymentConfirmationData,
        amount,
        currency,
        printOptions,
        onBackFromCheckout,
        ...rest
    }) => {
        if (!methods) return;

        /*
            Checked before anything else, and deliberately not tied to `currentStep` or
            `selectedMethod`: the money is already gone, so no navigation state should be
            able to put a payment form back on screen.
        */
        if (capturedPayment && currentStep !== PAYMENT_CONFIRMATION_STEP) {
            return (
                <PaymentFinalizing
                    capturedPayment={capturedPayment}
                    onRecorded={handlePaymentRecorded}
                />
            );
        }

        const onBack = () => {
            const prevStep = getPrevStep(steps, currentStep);
            if (prevStep) {
                setCurrentStep(prevStep)
            } else {
                onBackFromCheckout && onBackFromCheckout();
            }
        };

        switch(currentStep) {
        case QUICK_PAY_STEP:
            return (
                <QuickPayMenu
                    invoiceData={invoiceData}
                    onSubmit={() => setCurrentStep(getNextStep(steps, currentStep))}
                    onBack={getPrevStep(steps, currentStep) || onBackFromCheckout ? onBack : undefined}
                />
            )
        case AMOUNT_SELECTION_STEP:
            return (
                <AmountSelectionMenu
                    invoiceData={invoiceData}
                    defaultValue={balanceType}
                    onBack={getPrevStep(steps, currentStep) || onBackFromCheckout ? onBack : undefined}
                    onSubmit={(data) => {
                        const { balance_type } = data;
                        const amount = getInvoiceAmount({
                            balanceNetPendingPayment: invoiceData?.balance_net_pending_payment,
                            balanceType: balance_type || balanceType
                        });
                        onAmountToPayChange(amount);
                        setAmountToPay(amount);
                        balance_type && setBalanceType(balance_type);
                        setCurrentStep(getNextStep(steps, currentStep));
                    }}
                />
            );
        case PAYNENT_METHOD_SELECTION_STEP: // i.e. !selectedMethod
            return (
                <PaymentMethodsMenu
                    methods={methods}
                    onSelect={(method) => {
                        setSelectedMethod(method);
                        setCurrentStep(getNextStep(steps, currentStep));
                    }}
                    isBulkPay={isBulkPay}
                    onBack={getPrevStep(steps, currentStep) || onBackFromCheckout ? onBack : undefined}
                    {...(checkoutType === CHECKOUT_TYPES.MILESTONE ? {backBtnText : 'Back'} : {})}
                />
            );
        case PAYMENT_METHOD_STEP:
            return (
                <selectedMethod.component
                    onCancel={(getPrevStep(steps, currentStep) || onBackFromCheckout) ? onBack : undefined}
                    onSuccess={(data) => {
                        setPaymentConfirmationData(data);
                        onPaymentSuccess && onPaymentSuccess(data);
                        setCurrentStep(getNextStep(steps, currentStep));
                    }}
                    onPaymentCaptured={handlePaymentCaptured}
                    quickPayEnabled={hasQuickPayEnabled}
                    quickPayFeatureEnabled={hasQuickPayFeatureEnabled}
                    invoiceToken={invoiceToken}
                    invoiceTokens={invoiceTokens}
                    recipientName={paymentMethods?.legal_name}
                    recipientEmailAddress={recipientEmailAddress}
                    uuid={uuid || invoiceData.recipient_uuid}
                    isBulkPay={isBulkPay}
                    balance={{
                        ...(invoiceData?.balance_net_pending_payment || {amount: amount}),
                        currency
                    }}
                    printOptions={printOptions}
                    paymentMethods={paymentMethods}
                    onAmountToPayChange={onAmountToPayChange}
                    balanceType={balanceType}
                    {...rest}
                    {...checkoutData}/>
            )
        case PAYMENT_CONFIRMATION_STEP:
            return (
                <PaymentConfirmation
                    {...{
                        paymentConfirmationData,
                        invoiceData,
                        isBulkPay,
                        onReturnToMainMenu,
                        amount,
                        currency,
                        printOptions
                    }}
                />
            )
        }
    }

    return (isBulkPay || (amount > 0)) ? (
        <CheckoutCard
            /* Show total displayed at the top of the checkout widget */
            showTotal={!capturedPayment && [PAYMENT_METHOD_STEP, PAYNENT_METHOD_SELECTION_STEP].includes(currentStep)}
            amount={amountToPay}
            currency={currency}
            description={descriptionTextUnderTotal}>
            {renderCheckout({
                methods,
                selectedMethod,
                checkoutData,
                isBulkPay,
                wireData: paymentMethods?.payment_methods?.bank_transfer?.details?.type === 'international_wire' ? paymentMethods?.payment_methods?.bank_transfer?.details : undefined,
                onAmountToPayChange: setAmountToPay,
                onDescriptionUnderTotalChange: setDescriptionTextUnderSubtext,
                requireQuickPayOnCheckout,
                optionalQuickPayOnCheckout,
                isMilestoneInvoice,
                onBackFromCheckout,
                paymentConfirmationData,
                invoiceData,
                amount,
                currency,
                printOptions
            })}
        </CheckoutCard>
    ): (<></>);
};

export default Checkout;
